// #606: Portal für einzelne, ausgewählte Mieter/Eigentümer schon vor dem allgemeinen Start freischalten.
// Schalter je Person im Bereich „Portal“; wirkt über die Liste `testkonten` in app_settings 'portale_funktionen'
// (portal_freigeschaltet() lässt diese Profile ins Portal, auch wenn das Portal für alle aus ist).
import { useEffect, useState } from 'react';
import { Flask } from '@phosphor-icons/react';
import { SchalterZeile } from '../immobilien/ui/IosSwitch';
import { supabase } from '../../lib/supabase/client';
import { ladeTestphase, setzeTestphase, type PortalArt, type TestphaseStand } from '../../lib/portal/schalter';

interface Props {
  art: PortalArt;
  tabelle: 'mieter' | 'eigentuemer';
  datensatzId: string;
  name: string;
  zeige: (text: string, fehler?: boolean) => void;
}

export function TestphaseKarte({ art, tabelle, datensatzId, name, zeige }: Props) {
  const [profilId, setProfilId] = useState<string | null>(null);
  const [stand, setStand] = useState<TestphaseStand | null>(null);
  const [admin, setAdmin] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const wer = art === 'mieterportal' ? 'Mieter' : 'Eigentümer';

  useEffect(() => {
    let aktiv = true;
    void (async () => {
      const [{ data }, a] = await Promise.all([
        supabase.from(tabelle).select('profile_id').eq('id', datensatzId).maybeSingle(),
        supabase.rpc('is_admin'),
      ]);
      const pid = (data as { profile_id?: string | null } | null)?.profile_id ?? null;
      const s = pid ? await ladeTestphase(art, pid) : null;
      if (!aktiv) return;
      setProfilId(pid); setStand(s); setAdmin(a.data === true);
    })();
    return () => { aktiv = false; };
  }, [art, tabelle, datensatzId]);

  if (!profilId || !stand) return null;

  const umschalten = async (an: boolean) => {
    setLaeuft(true);
    const fehler = await setzeTestphase(profilId, an);
    setLaeuft(false);
    if (fehler) { zeige(`Nicht geändert: ${fehler}`, true); return; }
    setStand({ ...stand, frei: an });
    zeige(an ? `Portal für ${name} freigeschaltet – ab der nächsten Anmeldung sichtbar` : `Portal für ${name} wieder ausgeblendet`);
  };

  return (
    <div className="flex flex-col" style={{ gap: 8, padding: 12, borderRadius: 18, background: 'var(--fill)' }}>
      {stand.portalAn ? (
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Das {wer}portal ist für alle eingeschaltet – eine einzelne Freischaltung ist nicht nötig.</span>
      ) : (
        <>
          <SchalterZeile label="Portal schon jetzt freischalten (Testphase)" icon={Flask} an={stand.frei}
            onChange={v => { void umschalten(v); }} disabled={laeuft || !admin} />
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
            Das {wer}portal ist für alle noch aus. Eingeschaltet sieht nur diese Person nach dem Login schon ihr Portal – alle anderen weiter „Ihr Portal ist bald verfügbar“.
            Automatische Benachrichtigungs-Mails bleiben aus.{!admin ? ' Ändern können nur Admins.' : ''}
          </span>
        </>
      )}
    </div>
  );
}
